/**
 * Feature flags for reversible product surfaces.
 * Camp Essentials defaults OFF until post-initial-release.
 *
 * Enable with any of:
 * - REACT_APP_FEATURE_CAMP_ESSENTIALS=true
 * - localStorage key feature-camp-essentials=1
 * - URL ?feature-camp-essentials=1 (also persists to localStorage)
 */

export const FEATURE_CAMP_ESSENTIALS_STORAGE_KEY = 'feature-camp-essentials';
export const FEATURE_CAMP_ESSENTIALS_QUERY = 'feature-camp-essentials';

/** In-memory fallback when localStorage is unavailable (e.g. some Node contexts). */
const memoryStore = new Map();

function getBrowserStorage(windowRef) {
  try {
    if (windowRef?.localStorage) return windowRef.localStorage;
    if (typeof window !== 'undefined' && window.localStorage) return window.localStorage;
  } catch {
    /* ignore */
  }
  return null;
}

function storageGet(key, windowRef) {
  const ls = getBrowserStorage(windowRef);
  if (ls) {
    try {
      return ls.getItem(key);
    } catch {
      /* fall through */
    }
  }
  return memoryStore.has(key) ? memoryStore.get(key) : null;
}

function storageSet(key, value, windowRef) {
  const ls = getBrowserStorage(windowRef);
  if (ls) {
    try {
      if (value === null || value === undefined) ls.removeItem(key);
      else ls.setItem(key, value);
      return;
    } catch {
      /* fall through */
    }
  }
  if (value === null || value === undefined) memoryStore.delete(key);
  else memoryStore.set(key, value);
}

function readEnvFlag() {
  return (process.env.REACT_APP_FEATURE_CAMP_ESSENTIALS || '').toLowerCase() === 'true';
}

/**
 * Sync URL query override into storage (opt-in for local testing without rebuild).
 * ?feature-camp-essentials=1 enables; =0 clears storage override.
 */
export function syncCampEssentialsFlagFromUrl(
  windowRef = typeof window !== 'undefined' ? window : undefined
) {
  if (!windowRef?.location?.search) return;
  try {
    const params = new URLSearchParams(windowRef.location.search);
    if (!params.has(FEATURE_CAMP_ESSENTIALS_QUERY)) return;
    const raw = (params.get(FEATURE_CAMP_ESSENTIALS_QUERY) || '').toLowerCase();
    if (raw === '1' || raw === 'true') {
      storageSet(FEATURE_CAMP_ESSENTIALS_STORAGE_KEY, '1', windowRef);
    } else if (raw === '0' || raw === 'false') {
      storageSet(FEATURE_CAMP_ESSENTIALS_STORAGE_KEY, null, windowRef);
    }
  } catch {
    /* ignore */
  }
}

/** Test helper — clear memory + browser storage override. */
export function clearCampEssentialsFlagOverride(
  windowRef = typeof window !== 'undefined' ? window : undefined
) {
  storageSet(FEATURE_CAMP_ESSENTIALS_STORAGE_KEY, null, windowRef);
}

export function isCampEssentialsEnabled(
  windowRef = typeof window !== 'undefined' ? window : undefined
) {
  if (readEnvFlag()) return true;
  if (windowRef) syncCampEssentialsFlagFromUrl(windowRef);
  return storageGet(FEATURE_CAMP_ESSENTIALS_STORAGE_KEY, windowRef) === '1';
}
